import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Tab, Tabs, useMediaQuery } from "@mui/material";
import { getAhaCatalogAdmin, getAhaModulesAdmin, setAhaProductEnvironmentStatus } from "../../api/apiClient";
import AhaProductDialog from "./AhaProductDialog";
import AhaModulesPanel from "./AhaModulesPanel";
import CatalogStateBox from "./ahaCatalog/CatalogStateBox";
import ConfirmStatusDialog from "./ahaCatalog/ConfirmStatusDialog";
import Icon from "./ahaCatalog/Icon";
import ProductCards from "./ahaCatalog/ProductCards";
import ProductTable from "./ahaCatalog/ProductTable";
import StatStrip from "./ahaCatalog/StatStrip";
import { FilterMenu, Switch } from "./ahaCatalog/controls";
import {
  CERTIFICATE_STANDARDS, COURSE_FILTERS, FAMILIES, NO_EDITION, OTHER_FAMILY, SORTS, STATUS, familyOf, filterProducts, matchesCourseFilter, matchesStandard, sortProducts, standardOf, statusOf,
} from "./ahaCatalog/catalogModel";
import "./ahaCatalog/ahaCatalog.css";

const NARROW_QUERY = "(max-width:900px)";
const STATUS_OPTIONS = ["active", "verified", "unverified", "inactive"];
const statusLabel = (s) => STATUS[s].label;
const errorDetail = (err) => err?.response?.data?.message;

/** Global-admin screen to manage the shared AHA catalog (products, modules) and verify/activate products for the connected AHA environment. */
export default function AhaCatalogView({ courseLinks, onOpenCourse, onCreateCourse, onLinkCourse, reloadKey, addProductSignal = 0, AddDialog, onProductAdded }) {
  // Hosted on the Standard courses page: that page owns the title, the Add product button and the page-level notices.
  const hosted = Boolean(courseLinks);
  const narrow = useMediaQuery(NARROW_QUERY);
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [modules, setModules] = useState([]);
  const [tab, setTab] = useState("products");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [familyFilter, setFamilyFilter] = useState([]);
  const [editionFilter, setEditionFilter] = useState([]);
  const [courseFilter, setCourseFilter] = useState("all");
  const [standard, setStandard] = useState("all");
  const [sort, setSort] = useState("name");
  const [grouped, setGrouped] = useState(true);
  const [collapsed, setCollapsed] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bannerOpen, setBannerOpen] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [confirming, setConfirming] = useState(null);
  const [productDialog, setProductDialog] = useState(null);

  const load = useCallback(() => Promise.all([getAhaCatalogAdmin(), getAhaModulesAdmin()])
    .then(([data, moduleList]) => {
      setCatalog({ environment: data?.environment || "", products: data?.products || [] });
      setModules(Array.isArray(moduleList) ? moduleList : []);
      setLoadError(false);
    })
    .catch(() => setLoadError(true)), []);

  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => { if (addProductSignal > 0) setProductDialog({ mode: "create" }); }, [addProductSignal]);

  const retry = () => { setLoadError(false); load(); };

  /** Applies the flags to each product in turn, then reloads once. Failures are shown on their rows and stay selected. */
  const applyChanges = useCallback(async (products, flags) => {
    const failed = [];
    const errors = {};
    for (const product of products) {
      setSavingKey(product.key);
      try {
        // Sequential on purpose: each call is audited and the backend validates verified-before-active per product.
        // eslint-disable-next-line no-await-in-loop
        await setAhaProductEnvironmentStatus(product.key, catalog.environment, flags);
      } catch (err) {
        failed.push(product.key);
        const detail = errorDetail(err);
        errors[product.key] = `Could not update ${product.name}${detail ? `: ${detail}` : "."}`;
      }
    }
    setSavingKey(null);
    setRowErrors((prev) => {
      const cleared = Object.fromEntries(Object.entries(prev).filter(([key]) => !products.some((p) => p.key === key)));
      return { ...cleared, ...errors };
    });
    setSelected((prev) => prev.filter((key) => failed.includes(key)));
    await load();
  }, [catalog, load]);

  const products = useMemo(() => catalog?.products || [], [catalog]);
  const rows = useMemo(
    () => sortProducts(
      filterProducts(products, { query, statuses: statusFilter, families: familyFilter, editions: editionFilter })
        .filter((p) => matchesCourseFilter(courseFilter, courseLinks, p) && matchesStandard(standard, p)),
      sort,
    ),
    [products, query, statusFilter, familyFilter, editionFilter, courseFilter, standard, courseLinks, sort],
  );
  const unlinkedCount = courseLinks ? products.filter((p) => !courseLinks[p.key]).length : 0;
  const familyOptions = useMemo(() => [...FAMILIES, OTHER_FAMILY].filter((f) => products.some((p) => familyOf(p) === f)), [products]);
  const editionOptions = useMemo(() => {
    const editions = [...new Set(products.map((p) => p.edition).filter(Boolean))].sort().reverse();
    return products.some((p) => !p.edition) ? [...editions, NO_EDITION] : editions;
  }, [products]);

  const filterCount = statusFilter.length + familyFilter.length + editionFilter.length + (courseFilter !== "all" ? 1 : 0) + (standard !== "all" ? 1 : 0);
  const filtering = filterCount > 0 || query.trim() !== "";
  const clearAll = () => { setQuery(""); setStatusFilter([]); setFamilyFilter([]); setEditionFilter([]); setCourseFilter("all"); setStandard("all"); };
  const removeFrom = (list, setList, value) => setList(list.filter((x) => x !== value));

  const selectedProducts = products.filter((p) => selected.includes(p.key));
  const withStatus = (s) => selectedProducts.filter((p) => statusOf(p) === s);

  const requestToggle = (product, next) => setConfirming({ mode: next ? "activate" : "deactivate", products: [product] });
  const confirmChange = (mode, eligible) => { setConfirming(null); applyChanges(eligible, { active: mode === "activate" }); };

  if (loadError) return <div className="ac-root"><CatalogStateBox kind="error" onRetry={retry} /></div>;
  if (!catalog) return <div className="ac-root"><CatalogStateBox kind="loading" /></div>;
  if (!catalog.environment) {
    return (
      <Alert severity="warning">
        This backend is not connected to an AHA environment, so there is no catalog to manage here. Run the backend
        with a local, dev1, dev2, or prod profile.
      </Alert>
    );
  }

  const course = courseLinks ? { courseLinks, onOpenCourse, onCreateCourse, onLinkCourse } : undefined;
  const listProps = { course, rows, savingKey, errors: rowErrors, onVerify: (p) => applyChanges([p], { verified: true }), onToggle: requestToggle, onEdit: (p) => setProductDialog({ mode: "edit", product: p }) };

  return (
    <div className="ac-root">
      {!hosted && <div className="ac-head">
        <div className="ac-title">
          <h2>AHA product catalog</h2>
          {!bannerOpen && <button type="button" className="ac-ib" aria-label="About this catalog" onClick={() => setBannerOpen(true)}><Icon n="info" s={17} /></button>}
        </div>
        <div className="ac-head-r">
          <span className="ac-env"><Icon n="server" s={13} /><b>AHA {catalog.environment}</b></span>
          <button type="button" className="ac-btn" onClick={() => setProductDialog({ mode: "create" })}><Icon n="plus" s={14} w={2.5} />Add product</button>
        </div>
      </div>}

      {!hosted && bannerOpen && (
        <div className="ac-banner" role="note">
          <Icon n="info" s={17} />
          <p>
            This backend is connected to AHA {catalog.environment}. Only <b>active</b> products appear in Create Course. Verify each
            product number against AHA documentation before activating. Changes apply to every training center and are audit-logged.
          </p>
          <button type="button" className="ac-ib" aria-label="Dismiss" onClick={() => setBannerOpen(false)}><Icon n="x" s={16} /></button>
        </div>
      )}

      {courseLinks && unlinkedCount > 0 && (
        <div className="ac-banner" role="note" data-testid="no-course-notice">
          <Icon n="info" s={17} />
          <p>
            {unlinkedCount} {unlinkedCount === 1 ? "product has" : "products have"} no standard course yet. Create a course from the product, or link it to an
            existing course.
          </p>
        </div>
      )}

      {courseLinks && (
        <div className="ac-tools" role="group" aria-label="Certificate standard">
          <span className="ac-hint" style={{ fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>Certificate standard</span>
          {["all", ...CERTIFICATE_STANDARDS].map((value) => (
            <button key={value} type="button" className={`fchip${standard === value ? " on" : ""}`} aria-pressed={standard === value} onClick={() => setStandard(value)}>
              {value === "all" ? "All" : value}<span className="c">{value === "all" ? products.length : products.filter((p) => standardOf(p) === value).length}</span>
            </button>
          ))}
        </div>
      )}

      <StatStrip products={products} environment={catalog.environment} statusFilter={statusFilter} onStatusFilter={setStatusFilter} />

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: "1px solid var(--color-hairline-cloud)" }}>
        <Tab value="products" label="Products" />
        <Tab value="modules" label="Modules" />
      </Tabs>

      {tab === "modules" && <AhaModulesPanel modules={modules} environment={catalog.environment} onChanged={load} />}

      {tab === "products" && (
        <>
          {selected.length > 0 ? (
            <div className="ac-bulk" role="toolbar" aria-label="Bulk actions">
              <b>{selected.length} selected</b>
              <button type="button" disabled={!withStatus("unverified").length} onClick={() => applyChanges(withStatus("unverified"), { verified: true })}>
                <Icon n="shield" s={14} />Verify ({withStatus("unverified").length})
              </button>
              <button type="button" disabled={!withStatus("verified").length} onClick={() => setConfirming({ mode: "activate", products: selectedProducts })}>
                <Icon n="checkCircle" s={14} />Activate ({withStatus("verified").length})
              </button>
              <button type="button" disabled={!withStatus("active").length} onClick={() => setConfirming({ mode: "deactivate", products: selectedProducts })}>
                <Icon n="minusCircle" s={14} />Deactivate ({withStatus("active").length})
              </button>
              <span className="sp" />
              <button type="button" className="clr" onClick={() => setSelected([])}>Clear selection</button>
            </div>
          ) : (
            <div className="ac-tools">
              <label className="ac-search">
                <Icon n="search" s={15} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, key, or product number" aria-label="Search products" />
                {query && <button type="button" className="ac-ib sm" aria-label="Clear search" onClick={() => setQuery("")}><Icon n="x" s={14} /></button>}
              </label>
              <FilterMenu label="Status" options={STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter} render={statusLabel} />
              <FilterMenu label="Family" options={familyOptions} value={familyFilter} onChange={setFamilyFilter} />
              <FilterMenu label="Edition" options={editionOptions} value={editionFilter} onChange={setEditionFilter} render={(e) => (e === NO_EDITION ? "No edition" : e)} />
              <span className="sp" />
              {courseLinks && (
                <div className="seg" role="radiogroup" aria-label="Standard course">
                  {COURSE_FILTERS.map(([value, label]) => (
                    <button key={value} type="button" role="radio" aria-checked={courseFilter === value} className={courseFilter === value ? "on" : ""} onClick={() => setCourseFilter(value)}>
                      {label}{value === "unlinked" && <span className="c" style={{ marginLeft: 6, opacity: 0.7 }}>{unlinkedCount}</span>}
                    </button>
                  ))}
                </div>
              )}
              <label className="ac-sort">
                <Icon n="sort" s={14} /><span className="sr">Sort by</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  {Object.entries(SORTS).map(([key, text]) => <option key={key} value={key}>{text}</option>)}
                </select>
              </label>
              <label className="ac-grpt"><Switch on={grouped} onChange={setGrouped} label="Group by family" /><span>Group by family</span></label>
            </div>
          )}

          {selected.length === 0 && filtering && (
            <div className="ac-applied">
              <span className="n">{rows.length} of {products.length} products</span>
              {query.trim() && <span className="ac-chipx">“{query}”<button type="button" aria-label="Remove search" onClick={() => setQuery("")}><Icon n="x" s={12} /></button></span>}
              {statusFilter.map((s) => <span key={s} className="ac-chipx">{statusLabel(s)}<button type="button" aria-label={`Remove ${statusLabel(s)}`} onClick={() => removeFrom(statusFilter, setStatusFilter, s)}><Icon n="x" s={12} /></button></span>)}
              {familyFilter.map((f) => <span key={f} className="ac-chipx">{f}<button type="button" aria-label={`Remove ${f}`} onClick={() => removeFrom(familyFilter, setFamilyFilter, f)}><Icon n="x" s={12} /></button></span>)}
              {standard !== "all" && <span className="ac-chipx">{standard}<button type="button" aria-label="Remove certificate standard filter" onClick={() => setStandard("all")}><Icon n="x" s={12} /></button></span>}
              {courseFilter !== "all" && <span className="ac-chipx">{COURSE_FILTERS.find(([v]) => v === courseFilter)[1]}<button type="button" aria-label="Remove course filter" onClick={() => setCourseFilter("all")}><Icon n="x" s={12} /></button></span>}
              {editionFilter.map((e) => <span key={e} className="ac-chipx">{e === NO_EDITION ? "No edition" : e}<button type="button" aria-label={`Remove ${e}`} onClick={() => removeFrom(editionFilter, setEditionFilter, e)}><Icon n="x" s={12} /></button></span>)}
              <button type="button" className="ac-link" onClick={clearAll}>Clear all</button>
            </div>
          )}

          {rows.length === 0 ? <CatalogStateBox kind="empty" onReset={clearAll} />
            : narrow ? <ProductCards {...listProps} />
              : (
                <ProductTable {...listProps} grouped={grouped} collapsed={collapsed} onCollapsed={setCollapsed}
                  selected={selected} onSelected={setSelected} expanded={expanded} onExpanded={setExpanded} />
              )}
        </>
      )}

      {hosted && AddDialog && (
        <AddDialog open={productDialog?.mode === "create"} modules={modules} products={products} environment={catalog.environment}
          onSaved={(message) => { setProductDialog(null); load(); if (onProductAdded) onProductAdded(message); }}
          onClose={() => setProductDialog(null)} />
      )}
      <AhaProductDialog open={!!productDialog && !(hosted && AddDialog && productDialog.mode === "create")} mode={productDialog?.mode}
        product={productDialog?.product} modules={modules}
        environment={catalog.environment} onSaved={() => { setProductDialog(null); load(); }} onChanged={load}
        onClose={() => setProductDialog(null)} />

      <ConfirmStatusDialog request={confirming} environment={catalog.environment} onClose={() => setConfirming(null)} onConfirm={confirmChange} />
    </div>
  );
}
